// ─── Single source of truth for invoice / receipt template defaults ──────────
// Both the admin builder UI (StoreBrandingSettings) and the print/download
// renderer (receiptDownload) import these so the toggles, labels, ordering
// and the rendered HTML stay in lock-step.

export const DEFAULT_INVOICE_LABELS = {
  pageTitle:           'Invoice',
  heading:             'Invoice',
  badge:               'Invoice',
  invoiceNumber:       'Invoice #',
  date:                'Date',
  dueDate:             'Due date',
  salesPerson:         'Sales person',
  technician:          'Technician',
  store:               'Store',
  status:              'Status',
  customer:            'Customer',
  phone:               'Phone',
  genderAge:           'Gender / Age',
  birthday:            'Birthday',
  visitNumber:         'Visit #',
  frame:               'Frame',
  lens:                'Lens',
  timeToDeliver:       'Time to deliver',
  itemsHeading:        'Items',
  measurementsHeading: 'Measurements',
  colDescription:      'Description',
  colLensType:         'Lens type',
  colSphCyl:           'SPH / CYL',
  colQty:              'Qty',
  colUnitPrice:        'Unit price',
  colTotal:            'Amount',
  subtotal:            'Subtotal',
  discount:            'Discount',
  total:               'Total',
  amountPaid:          'Paid',
  remaining:           'Balance due',
  rxPrefix:            'Rx',
  qrCaption:           '',
};

export const DEFAULT_RECEIPT_LABELS = {
  pageTitle:           'Customer receipt',
  heading:             'Sales receipt',
  badge:               'Customer copy',
  orderNumber:         'Order number',
  date:                'Date',
  salesPerson:         'Sales person',
  store:               'Store',
  status:              'Status',
  customer:            'Customer',
  phone:               'Phone',
  birthday:            'Birthday',
  frame:               'Frame',
  lens:                'Lens',
  correction:          'Correction',
  stamp:               'Stamp',
  timeToDeliver:       'Time to deliver',
  itemsHeading:        'Items',
  measurementsHeading: 'Measurements',
  colDescription:      'Description',
  colLensType:         'Lens type',
  colSphCyl:           'SPH / CYL',
  colQty:              'Qty',
  colUnitPrice:        'Unit price',
  colTotal:            'Amount',
  subtotal:            'Subtotal',
  discount:            'Discount',
  total:               'Total',
  payment:             'Payment',
  amountPaid:          'Advance payment',
  remaining:           'Rest to pay',
  rxPrefix:            'Rx',
  qrCaption:           '',
};

export const DEFAULT_INVOICE_TEMPLATE = {
  sectionOrder:        ['header', 'meta', 'client', 'items', 'totals', 'footer'],
  // Header
  showLogo:            true,
  headerLogoPosition:  'left',
  showTagline:         true,
  showStorePhone:      true,
  showStoreAddress:    false,
  // Meta
  showInvoiceNumber:   true,
  showDate:            true,
  showDueDate:         false,
  showSalesPerson:     true,
  showTechnician:      false,
  showStore:           true,
  showStatus:          false,
  showQR:              true,
  // Client
  showClientName:      true,
  showClientPhone:     true,
  showClientGenderAge: true,
  showClientBirthday:  false,
  showVisitNumber:     false,
  showFrame:           true,
  showLens:            true,
  showTimeToDeliver:   false,
  // Items columns
  showColItemName:     true,
  showColLensType:     true,
  showColSphCyl:       true,
  showColQty:          true,
  showColUnitPrice:    true,
  showColTotal:        true,
  showLensPrescription: false,
  showMeasurements:    false,
  // Totals
  showSubtotal:        true,
  showDiscount:        true,
  showAmountPaid:      true,
  showRemainingBalance: true,
  // Footer
  footerMessage:       'Thank you for choosing us!',
  showFooterPhone:     true,
  showFooterAddress:   false,
  showFooterWebsite:   false,
  footerWebsite:       '',
  footerFontSize:      'medium',
  // Editable labels
  labels:              { ...DEFAULT_INVOICE_LABELS },
};

export const DEFAULT_RECEIPT_TEMPLATE = {
  // Header
  showLogo:                true,
  storeNameSize:           'large',
  showAddress:             true,
  showPhone:               true,
  showEmail:               true,
  showTagline:             true,
  // Meta
  showOrderNumber:         true,
  showDate:                true,
  showStore:               true,
  showStatus:              true,
  showSalesPerson:         true,
  showQR:                  true,
  // Customer
  showCustomer:            true,
  showCustomerPhone:       true,
  showCustomerBirthday:    true,
  // The approved customer copy shows the correction only; frame and lens stay
  // available as opt-in rows for stores that want them itemised.
  showFrame:               false,
  showLens:                false,
  showCorrection:          true,
  showTimeToDeliver:       true,
  // Items
  showItemsTable:          true,
  showColLensType:         false,
  showColSphCyl:           false,
  showColQty:              true,
  showColUnitPrice:        false,
  showColTotal:            true,
  showPrescriptionDetails: false,
  showMeasurements:        true,
  // Totals
  showSubtotal:            false,
  showDiscount:            false,
  showTotal:               true,
  showPayment:             true,
  showAmountPaid:          true,
  showRemainingBalance:    true,
  // Signature / stamp
  showStampBox:            true,
  // Footer
  footerMessage:           'Thank you for your visit!',
  showReturnPolicy:        false,
  returnPolicyText:        'Returns accepted within 7 days with receipt.',
  // Collection terms printed under the thank-you line. {{store}} is replaced
  // with the store name at render time.
  showCollectionTerms:     true,
  collectionTermsText:     'The equipment must be collected within 3 months of the order date. Beyond this period, {{store}} is not responsible for its loss, and any deposit paid will be permanently lost.',
  fontSize:                'medium',
  // Editable labels
  labels:                  { ...DEFAULT_RECEIPT_LABELS },
};

// ─── Order-number format tokens ──────────────────────────────────────────────
//  {YY}    – 2-digit year     (26)
//  {YYYY}  – 4-digit year     (2026)
//  {MM}    – 2-digit month    (05)
//  {DD}    – 2-digit day      (08)
//  {CITY}  – 2-digit city code (12)
//  {STORE} – 2-digit store code (01)
//  {DAYSEQ}  – sequence of the order that day, default 3 digits
//  {DAYSEQN} – daily sequence padded to N digits  e.g. {DAYSEQ3} → 001
//  {SEQ}     – global counter, default 6 digits
//  {SEQN}    – global counter padded to N digits  e.g. {SEQ6} → 000005
export const DEFAULT_ORDER_NUMBER_FORMAT = 'ORD-{YY}-{MM}-{DD}-{CITY}-{STORE}-{DAYSEQ3}';
const OLD_DEFAULT_ORDER_NUMBER_FORMAT = 'ORD-{YY}{MM}{DD}{CITY}{STORE}{SEQ3}';

function normalizeOrderNumberFormat(format) {
  const value = String(format || '').trim();
  if (!value || value === OLD_DEFAULT_ORDER_NUMBER_FORMAT) return DEFAULT_ORDER_NUMBER_FORMAT;
  return value;
}

// Resolve an order-number format string client-side for live previews. Server
// uses the same tokens (see server/utils/orderNumber.js) but also queries the
// real per-store sequence counter.
export function previewOrderNumber(format = DEFAULT_ORDER_NUMBER_FORMAT, ctx = {}) {
  const now    = ctx.date ? new Date(ctx.date) : new Date();
  const yy     = String(now.getFullYear()).slice(-2);
  const yyyy   = String(now.getFullYear());
  const mm     = String(now.getMonth() + 1).padStart(2, '0');
  const dd     = String(now.getDate()).padStart(2, '0');
  const city   = String(ctx.cityCode  ?? '12').replace(/\D/g, '').padStart(2, '0').slice(-2);
  const store  = String(ctx.storeCode ?? '01').replace(/\D/g, '').padStart(2, '0').slice(-2);
  const seq    = ctx.sequence ?? 0;
  const daySeq = ctx.daySequence ?? seq;

  return normalizeOrderNumberFormat(format)
    .replace(/\{YYYY\}/g, yyyy)
    .replace(/\{YY\}/g,   yy)
    .replace(/\{MM\}/g,   mm)
    .replace(/\{DD\}/g,   dd)
    .replace(/\{CITY\}/g, city)
    .replace(/\{STORE\}/g, store)
    .replace(/\{DAYSEQ(\d*)\}/g, (_, n) => {
      const pad = parseInt(n, 10) || 3;
      return String(daySeq).padStart(pad, '0');
    })
    .replace(/\{SEQ(\d*)\}/g, (_, n) => {
      const pad = parseInt(n, 10) || 6;
      return String(seq).padStart(pad, '0');
    });
}
